import { useState } from "react";
import { PrimaryButton } from "./Button";
import { LoadingDots } from "./LoadingDots";
import { SheetContainer } from "./SheetContainer";
import { strings } from "../strings";

// AlertCircle icon SVG
function AlertCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  );
}

export interface InterruptedSheetProps {
  onReconnect: () => Promise<void>;
  isLoading?: boolean;
  className?: string;
}

export function InterruptedSheet({
  onReconnect,
  isLoading = false,
  className,
}: InterruptedSheetProps) {
  const [isReconnecting, setIsReconnecting] = useState(false);

  const handleReconnect = async () => {
    setIsReconnecting(true);
    try {
      await onReconnect();
    } finally {
      setIsReconnecting(false);
    }
  };

  const loading = isLoading || isReconnecting;

  return (
    <SheetContainer className={`gap-6 ${className || ""}`}>
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <AlertCircleIcon className="w-6 h-6 text-yellow-600" />
          <h2 className="text-2xl font-semibold text-foreground">
            {strings.interruptedSheet.title}
          </h2>
        </div>

        <div className="text-muted-foreground text-left flex flex-col gap-3">
          <p className="text-base">{strings.interruptedSheet.description1}</p>
          <p className="text-base">{strings.interruptedSheet.description2}</p>
        </div>
      </div>

      {/* Reconnect button */}
      <div>
        <PrimaryButton
          onClick={handleReconnect}
          disabled={loading}
          className="w-full"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <LoadingDots size="sm" />
              {strings.interruptedSheet.buttonReconnecting}
            </span>
          ) : (
            strings.interruptedSheet.buttonReconnect
          )}
        </PrimaryButton>
      </div>
    </SheetContainer>
  );
}
